import { getContactCrmSides } from "../../@types/sharedSchema";

/**
 * THE ONE definition of "does this contact get the BUYER-side surfaces?".
 *
 * Three surfaces ask, and they must answer identically or the UI contradicts itself:
 *   1. the contact page / CRM side panel's התאמות TAB (`detail/tabs/registry.ts`);
 *   2. the CRM row's matches INDICATOR (`crm/row/crmMatchesModel.ts`) — which offers a
 *      click into that very tab, so a disagreement is a click into something absent;
 *   3. the סקירה tab's ✦ פרטי חיפוש panel (`detail/tabs/OverviewTab.tsx`).
 *
 * (1) and (2) each carried their own copy of the rule, bound only by a comment saying
 * they were "byte-for-byte" the same. A third copy is how three copies come to disagree,
 * so the body lives here once and they call it.
 *
 * ── THE RULE ────────────────────────────────────────────────────────────────────────
 *
 * A PURE seller-side contact — מוכר/משכיר with no קונה/שוכר — hides them. Those surfaces
 * are all built around what a person is LOOKING FOR; a contact who is only selling has no
 * search to describe, and what that record is about is the property they hold (the seller
 * lens's listing card) rather than criteria.
 *
 * ⚠️ THE HIDE IS UNCONDITIONAL ON THE DATA, and that is the whole instruction (customer
 * reject, 2026-08-25: *"for a SELLER/LANDLORD that is NOT a buyer/renter we don't need to
 * show the פרטי חיפוש — even if defined"*). A מוכר carrying a leftover budget and a city
 * from an old import must still hide: rendering them presents somebody else's import as
 * this person's requirements, which is worse than showing nothing. So callers gate on WHO
 * THE CONTACT IS, never on whether the fields happen to be empty.
 *
 * A MIXED-type contact KEEPS them. A מוכר who is also a קונה is genuinely both — selling
 * one flat and buying another — and dropping the search profile there would lose exactly
 * the half of the record the realtor is working.
 *
 * An UNTYPED contact KEEPS them, and so does one typed only מתווך/עו"ד/שמאי (those map to
 * no pipeline side at all). Absence of a type is not evidence of being a seller: most of
 * the book carries no customerType, so failing closed here would hide the search profile
 * from the majority of contacts. Only an UNAMBIGUOUS seller hides.
 */

/**
 * Anything that can name a contact's customer types: the full `RealEstateContact` (which
 * may carry only the legacy `customerType` scalar), or a `CrmContactRow`-shaped row whose
 * `customerTypes` arrives off the wire as raw `string[]`. Deliberately structural, so no
 * caller has to import the contact type to ask the question.
 */
export type ContactCustomerTypesSource =
  | {
      customerType?: string | null;
      customerTypes?: readonly string[] | null;
    }
  | null
  | undefined;

/** True when the contact gets the buyer-side surfaces — see the rule above. */
export function contactShowsBuyerSurfaces(contact: ContactCustomerTypesSource): boolean {
  // The narrowing cast is the price of accepting a row whose `customerTypes` is `string[]`
  // rather than `CustomerType[]`, and it is safe in the direction that matters: an
  // unrecognized string maps to NO pipeline side (crmSideForCustomerType returns null), so
  // wire garbage can only ever leave the contact looking untyped — which keeps the
  // surfaces, the non-destructive answer. It replaces the `as never` the two original call
  // sites each carried.
  const sides = getContactCrmSides(contact as Parameters<typeof getContactCrmSides>[0]);
  return !(sides.includes("seller") && !sides.includes("buyer"));
}
